import { FEE_MGMT_COLUMNS, OVERRIDE_COLUMNS_KEY_TO_SNAKE_KEY, ROW_NUMBER_COL } from './Columns'
import { ZIP, COUNTY, STATE } from './Validation'
import { REQUIRED_COLUMNS_BY_OVERRIDE_TYPE_SNAKE_CASE, getRequiredEntityTypesByOverrideType } from './OverrideTypes'
import { normalizeStatesInput, normalizeCountiesForState } from '../utils/stateHelpers'

const LOCATION_TYPE_API_TO_READABLE = {
  zip: ZIP,
  county: COUNTY,
  multistate: STATE,
}

const LOCATION_TYPE_READABLE_TO_API = Object.fromEntries(
  Object.entries(LOCATION_TYPE_API_TO_READABLE).map(([key, value]) => [value, key]),
)

const getPanelistNameById = (panelistId, panelistMappingData) => {
  const panelist = panelistMappingData.find((p) => p.id === panelistId)
  return panelist ? panelist.name : panelistId
}

const getPanelistIdByName = (panelistName, panelistMappingData) => {
  const panelist = panelistMappingData.find((p) => p.name === panelistName)
  return panelist ? panelist.id : panelistName
}

const getProductNameById = (productId, productMappingData) => {
  const product = productMappingData.find((p) => p.id === productId)
  return product ? product.description : productId
}

const getProductIdByName = (productName, productMappingData) => {
  const product = productMappingData.find((p) => p.description === productName)
  return product ? product.id : productName
}

const getBranchNameById = (branchId, branchMappingData) => {
  const branch = branchMappingData.find((b) => b.id === branchId)
  return branch ? branch.name : branchId
}

const getBranchIdByName = (branchName, branchMappingData) => {
  const branch = branchMappingData.find((b) => b.name === branchName)
  return branch ? branch.id : branchName
}

const mapCountyToFips = (countyName, states, stateAndCountiesMap) => {
  if (!stateAndCountiesMap || !states.length) {
    return countyName
  }

  const countyNameLower = countyName.toLowerCase()

  // Try primary state first (most common case)
  const primaryState = states[0]
  const primaryStateMapping = stateAndCountiesMap[primaryState]?.counties
  if (primaryStateMapping) {
    const countyKey = Object.keys(primaryStateMapping).find(
      (key) => primaryStateMapping[key].readable.toLowerCase() === countyNameLower,
    )
    if (countyKey) {
      return primaryStateMapping[countyKey].fips_code
    }
  }

  // Try other states if needed
  for (let i = 1; i < states.length; i++) {
    const stateMapping = stateAndCountiesMap[states[i]]?.counties
    if (stateMapping) {
      const countyKey = Object.keys(stateMapping).find(
        (key) => stateMapping[key].readable.toLowerCase() === countyNameLower,
      )
      if (countyKey) {
        return stateMapping[countyKey].fips_code
      }
    }
  }

  return countyName
}

const mapCommaSeparatedStringToArray = (commaSeparatedString) => {
  return (
    commaSeparatedString
      ?.split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '') || []
  )
}

// Memoized function to create FIPS code lookup maps
const createFipsLookupMaps = (locationMappingData) => {
  const stateFipsToKey = {}
  const countyFipsToReadable = {} // Will now be nested: state_fips -> county_fips -> readable

  if (locationMappingData) {
    Object.entries(locationMappingData).forEach(([stateKey, stateData]) => {
      const stateFips = stateData.fips_code
      stateFipsToKey[stateFips] = stateKey

      if (stateData.counties) {
        // Initialize the nested structure for this state
        countyFipsToReadable[stateFips] = {}

        Object.entries(stateData.counties).forEach(([countyKey, countyData]) => {
          countyFipsToReadable[stateFips][countyData.fips_code] = countyData.readable
        })
      }
    })
  }

  return { stateFipsToKey, countyFipsToReadable }
}

// Memoized function to map a single item to grid format
const mapItemToGridFormat = (item, index, stateFipsToKey, countyFipsToReadable, mappingData) => {
  // Start with required grid management fields
  const baseFields = {
    [ROW_NUMBER_COL.columnKey]: index + 1,
    // Preserve the server-assigned override id so edits/deletes target the existing
    // document (UpdateOne) instead of inserting a duplicate. New rows have no _id.
    _id: item._id,
    [FEE_MGMT_COLUMNS.DELETE.readable]: false,
    [FEE_MGMT_COLUMNS.ERROR.readable]: false,
    validationErrors: {},
    userMarkedForDelete: false,
    isDuplicateRow: false,
  }

  // Map states from FIPS codes to state keys
  const mappedStates = item.states?.map((fipsCode) => stateFipsToKey[fipsCode] || fipsCode) || []

  // Map counties from FIPS codes to readable names, considering state context
  const mappedCounties =
    item.counties?.map((countyFips) => {
      const stateFips = item.states?.[0] // There should only be one state if it has a county mapping
      if (stateFips && countyFipsToReadable[stateFips]?.[countyFips]) {
        return countyFipsToReadable[stateFips][countyFips]
      }
      return countyFips // Fallback to original value if mapping not found
    }) || []

  // Map API fields to column keys
  const mappedFields = {
    [FEE_MGMT_COLUMNS.PRODUCT_NAME.columnKey]: getProductNameById(item.product_id, mappingData.products),
    [FEE_MGMT_COLUMNS.BRANCH.columnKey]: getBranchNameById(item.branch_id, mappingData.branches),
    [FEE_MGMT_COLUMNS.VENDOR.columnKey]: getPanelistNameById(item.panelist_id, mappingData.panelists),
    [FEE_MGMT_COLUMNS.LOCATION_TYPE.columnKey]: LOCATION_TYPE_API_TO_READABLE[item.location_type] || '',
    [FEE_MGMT_COLUMNS.STATES.columnKey]: mappedStates.join(','),
    [FEE_MGMT_COLUMNS.COUNTIES.columnKey]: mappedCounties.join(','),
    [FEE_MGMT_COLUMNS.ZIP_CODES.columnKey]: Array.isArray(item.zips) ? item.zips.join(',') : '',
    [FEE_MGMT_COLUMNS.FEE.columnKey]: item.override_data?.fee,
    [FEE_MGMT_COLUMNS.DUE_DATE.columnKey]: item.override_data?.due_date,
    [FEE_MGMT_COLUMNS.RUSH_DUE_DATE.columnKey]: item.override_data?.rush_due_date,
  }

  return {
    ...baseFields,
    ...mappedFields,
  }
}

// Build a blank grid row for manual entry (the "Add Row" action). Mirrors the
// management fields of an API-mapped row but with empty values and NO `_id`, so
// it persists as an insert. `rowNumber` is the grid's row id and must be unique.
const buildBlankRow = (rowNumber) => ({
  [ROW_NUMBER_COL.columnKey]: rowNumber,
  [FEE_MGMT_COLUMNS.DELETE.readable]: false,
  [FEE_MGMT_COLUMNS.ERROR.readable]: false,
  validationErrors: {},
  userMarkedForDelete: false,
  isDuplicateRow: false,
  [FEE_MGMT_COLUMNS.PRODUCT_NAME.columnKey]: '',
  [FEE_MGMT_COLUMNS.BRANCH.columnKey]: '',
  [FEE_MGMT_COLUMNS.VENDOR.columnKey]: '',
  [FEE_MGMT_COLUMNS.LOCATION_TYPE.columnKey]: '',
  [FEE_MGMT_COLUMNS.STATES.columnKey]: '',
  [FEE_MGMT_COLUMNS.COUNTIES.columnKey]: '',
  [FEE_MGMT_COLUMNS.ZIP_CODES.columnKey]: '',
  [FEE_MGMT_COLUMNS.FEE.columnKey]: '',
  [FEE_MGMT_COLUMNS.DUE_DATE.columnKey]: '',
  [FEE_MGMT_COLUMNS.RUSH_DUE_DATE.columnKey]: '',
})

// Optimized mapApiDataToGridData function
const mapApiDataToGridData = (data, mappingData) => {
  // Early return for invalid data
  if (!Array.isArray(data)) {
    return []
  }

  // Create FIPS lookup maps once
  const { stateFipsToKey, countyFipsToReadable } = createFipsLookupMaps(mappingData.state_and_counties_map)

  // Map all items to grid format
  return data.map((item, index) => mapItemToGridFormat(item, index, stateFipsToKey, countyFipsToReadable, mappingData))
}

// Map override-data column keys to their API snake_case keys. Non-data columns
// (entity/location columns like states, zipCodes) are not override_data fields and
// are dropped — otherwise they'd add an `undefined` key to override_data and fail
// backend validation. (The View page passes entity columns in selectedFields for
// display; only the true data fields belong in override_data.)
const mapOverrideFieldsToApiFields = (fieldsToOverride) => {
  return fieldsToOverride
    .map((field) => (field in OVERRIDE_COLUMNS_KEY_TO_SNAKE_KEY ? OVERRIDE_COLUMNS_KEY_TO_SNAKE_KEY[field] : undefined))
    .filter(Boolean)
}

const createBaseOverride = (overrideFieldsMap, requiredEntityColumns) => {
  // Create base override structure
  const baseOverride = requiredEntityColumns.reduce((acc, column) => {
    acc[column] = null
    return acc
  }, {})
  baseOverride.override_data = {}

  // Add override field keys directly to baseOverride with null values
  Object.values(overrideFieldsMap).forEach((field) => {
    baseOverride.override_data[field] = null
  })

  return baseOverride
}

// Function to map data between grid and API formats
const mapGridDataToApi = (data, mappingData, fieldsToOverride, overrideType) => {
  if (!Array.isArray(data)) {
    return []
  }

  const requiredEntityColumns = [
    ...REQUIRED_COLUMNS_BY_OVERRIDE_TYPE_SNAKE_CASE.common,
    ...REQUIRED_COLUMNS_BY_OVERRIDE_TYPE_SNAKE_CASE[overrideType],
  ]

  const entityTypes = getRequiredEntityTypesByOverrideType(overrideType)
  const overrideFields = mapOverrideFieldsToApiFields(fieldsToOverride)
  const processors = getSaveToApiProcessors(mappingData, [...requiredEntityColumns, ...overrideFields])
  const baseOverride = createBaseOverride(overrideFields, requiredEntityColumns)

  const overrides = data.reduce((acc, row) => {
    // A row flagged for deletion: if it exists on the server (_id), emit a delete
    // marker the backend soft-deletes; if it was added-then-deleted in this session,
    // drop it entirely (nothing to persist).
    if (row.userMarkedForDelete) {
      if (row._id) {
        acc.push({ _id: row._id, deleted: true })
      }
      return acc
    }

    // Create a deep copy for override_data to prevent shared reference issues
    const override = { ...baseOverride, override_data: { ...baseOverride.override_data } }
    const cache = {} // For cross-field processing

    // Hydrate using processors
    Object.entries(processors).forEach(([snakeKey, processor]) => {
      const columnKey = Object.values(FEE_MGMT_COLUMNS).find((column) => column.snakeCaseKey === snakeKey)?.columnKey

      if (columnKey && row[columnKey] !== undefined) {
        const value = processor(row[columnKey], cache)

        // Determine where to store the value
        if (snakeKey in baseOverride) {
          override[snakeKey] = value
        } else if (snakeKey in baseOverride.override_data) {
          override.override_data[snakeKey] = value
        }
      }
    })

    // Carry the server id so an existing row updates in place (UpdateOne) rather
    // than inserting a duplicate. New rows have no _id and are inserted.
    if (row._id) {
      override._id = row._id
    }

    acc.push(override)
    return acc
  }, [])

  return { entityTypes, dataTypes: Object.values(overrideFields), overrides }
}

const getSaveToApiProcessors = (mappingData, requiredColumns) => {
  const processors = {
    [FEE_MGMT_COLUMNS.PRODUCT_NAME.snakeCaseKey]: (rawValue) => getProductIdByName(rawValue, mappingData.products),
    [FEE_MGMT_COLUMNS.BRANCH.snakeCaseKey]: (rawValue) => getBranchIdByName(rawValue, mappingData.branches),
    [FEE_MGMT_COLUMNS.VENDOR.snakeCaseKey]: (rawValue) => getPanelistIdByName(rawValue, mappingData.panelists),
    [FEE_MGMT_COLUMNS.LOCATION_TYPE.snakeCaseKey]: (rawValue) => LOCATION_TYPE_READABLE_TO_API[rawValue] || null,
    [FEE_MGMT_COLUMNS.STATES.snakeCaseKey]: (rawValue, cache) => {
      const statesRaw = mapCommaSeparatedStringToArray(rawValue)

      // Normalize states to handle both abbreviations and full names (case-insensitive)
      const { normalizedStates } = normalizeStatesInput(statesRaw, mappingData?.state_and_counties_map)

      cache.statesRaw = normalizedStates
      return normalizedStates.map(
        (stateCode) => mappingData?.state_and_counties_map?.[stateCode]?.fips_code || stateCode,
      )
    },
    [FEE_MGMT_COLUMNS.COUNTIES.snakeCaseKey]: (rawValue, cache) => {
      if (!cache.statesRaw || cache.statesRaw.length === 0) {
        return mapCommaSeparatedStringToArray(rawValue)
      }

      const countiesRaw = mapCommaSeparatedStringToArray(rawValue)
      const primaryState = cache.statesRaw[0] // Counties are associated with a single state

      // Normalize counties to handle case-insensitive and special character matching
      const { normalizedCounties } = normalizeCountiesForState(
        countiesRaw,
        primaryState,
        mappingData?.state_and_counties_map,
      )

      return normalizedCounties.map((countyName) =>
        mapCountyToFips(countyName, cache.statesRaw, mappingData?.state_and_counties_map),
      )
    },
    [FEE_MGMT_COLUMNS.ZIP_CODES.snakeCaseKey]: (rawValue) => mapCommaSeparatedStringToArray(rawValue),
    // Override columns use their snakeCaseKey directly
    [FEE_MGMT_COLUMNS.FEE.snakeCaseKey]: (rawValue) => {
      return rawValue != null && rawValue !== '' ? parseFloat(rawValue) : undefined
    },
    [FEE_MGMT_COLUMNS.DUE_DATE.snakeCaseKey]: (rawValue) => {
      return rawValue != null && rawValue !== '' ? parseInt(rawValue, 10) : undefined
    },
    [FEE_MGMT_COLUMNS.RUSH_DUE_DATE.snakeCaseKey]: (rawValue) => {
      return rawValue != null && rawValue !== '' ? parseInt(rawValue, 10) : undefined
    },
  }

  return Object.fromEntries(Object.entries(processors).filter(([key]) => requiredColumns.includes(key)))
}

export { mapApiDataToGridData, mapGridDataToApi, buildBlankRow }
